import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, post } from './api';

/**
 * "Wunderland POS" on System status (owner 10/31: Avalon and Milwaukie, "similar to the apex version"), admins only: can
 * MyWunderland read each store's POS (SQL Server, read-only login), when it last did, and the main totals it read for
 * yesterday and today. "Test POS connection" reads it now. The app also reads it every 15 minutes and texts admins.
 */
interface Day { label: string; day: string; at: string | null; rows: [string, string][] }
interface Store { store: string; host: string; port: number; database: string; setUp: boolean; state: 'ok' | 'warn' | 'bad' | 'off'; summary: string; problem: string | null; days: Day[] }
const DOT: Record<string, string> = { ok: '#1E8E4E', warn: '#C27C0E', bad: '#C0392B', off: '#9AA3AC' };

export function PosStatus({ showToast }: { showToast: (m: string) => void }) {
  const [d, setD] = useState<{ stores: Store[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => { try { setD(await get<{ stores: Store[] }>('/api/admin/pos')); } catch { /* the rest of the page still works */ } }, []);
  useEffect(() => { void load(); }, [load]);
  const test = async (store: string) => {
    setBusy(store);
    try { const r = await post<{ ok: boolean; message: string }>('/api/admin/pos/test', { store }); showToast(r.message); await load(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : 'The test didn’t run.'); }
    finally { setBusy(null); }
  };
  if (!d || !d.stores.length) return null;
  return (
    <div className="stcard" id="pos">
      <span className="tlabel">Wunderland POS (sales for Reports)</span>
      <span style={{ color: '#4A5561', fontSize: 14 }}>
        Avalon’s and Milwaukie’s paperwork fills in from the Wunderland POS: MyWunderland signs in to the POS computer with a read-only login and
        asks for the same totals as the POS’s Closing Paperwork screen. It reads them every 15 minutes in the day (with the kiosk’s credit sales)
        and texts admins if it stops working.
      </span>
      {d.stores.map((s) => (
        <div key={s.store} className="apexstore">
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, background: DOT[s.state] }} />
            <b style={{ fontSize: 16 }}>{s.store}</b>
            <span style={{ color: s.state === 'bad' ? DOT.bad : '#4A5561' }}>{s.summary}</span>
            <span style={{ flex: '1 1 auto' }} />
            {s.setUp && <button className="btn2" disabled={busy !== null} onClick={() => void test(s.store)}>{busy === s.store ? 'Testing…' : 'Test POS connection'}</button>}
          </span>
          {s.problem && (
            <span style={{ fontSize: 13, color: '#7A4A06' }}>
              Last error: {s.problem}. Usually the POS computer is off, its firewall doesn’t let the NAS in on port {s.port}, or the login or password in the
              settings file has a typo.
            </span>
          )}
          {s.days.filter((x) => x.rows.length).map((x) => (
            <div key={x.label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 14 }}><b>Read from the POS · {x.label} {x.day}</b>{x.at && <span style={{ color: '#4A5561' }}> (read {x.at})</span>}</span>
              <table className="apexregs"><tbody>
                {x.rows.map(([k, v]) => <tr key={k}><td>{k}</td><td style={{ textAlign: 'right' }}>{v}</td></tr>)}
              </tbody></table>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
